import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Download, Upload, KeyRound } from "lucide-react";
import { api, formatApiError, getAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import ImageUpload from "../components/ImageUpload";

export default function Settings() {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="p-10 text-slate-400">Loading…</div>;

  const upd = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const downloadBackup = async () => {
    const auth = getAuth();
    const res = await api.get("/backup", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lovely-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    if (!auth) return;
  };

  const restore = async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post("/backup/restore", data);
      toast.success("Backup restored");
      window.location.reload();
    } catch (e) {
      toast.error("Restore failed — invalid file");
    }
  };

  const changePw = async () => {
    if (!pw.current || !pw.next) return toast.error("Fill both password fields");
    if (pw.next !== pw.confirm) return toast.error("Passwords do not match");
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <header className="mb-6">
        <p className="text-sm font-medium text-blue-800 uppercase tracking-wider">System</p>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Settings</h1>
        <p className="text-slate-500 mt-1">
          Institute details are applied to every certificate automatically.
        </p>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Institute profile</h3>
          <Button onClick={save} data-testid="settings-save" className="bg-blue-800 hover:bg-blue-900 h-10">
            <Save size={16} className="mr-2"/> Save changes
          </Button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Institute name">
            <Input value={s.institute_name} onChange={(e) => upd("institute_name", e.target.value)}
              className="h-10" data-testid="s-name"/>
          </Field>
          <Field label="Registration / Act information">
            <Input value={s.registration_info || ""} onChange={(e) => upd("registration_info", e.target.value)}
              className="h-10" data-testid="s-reginfo"
              placeholder="e.g., Regd. under Societies Registration Act XXI of 1860"/>
          </Field>
          <Field label="Certificate verification email">
            <Input value={s.verification_email || ""} onChange={(e) => upd("verification_email", e.target.value)}
              className="h-10" data-testid="s-vemail" placeholder="verify@example.com"/>
          </Field>
          <Field label="Website">
            <Input value={s.website} onChange={(e) => upd("website", e.target.value)}
              className="h-10" data-testid="s-website" placeholder="www.example.com"/>
          </Field>
          <Field label="Phone number">
            <Input value={s.phone} onChange={(e) => upd("phone", e.target.value)}
              className="h-10" data-testid="s-phone"/>
          </Field>
          <Field label="Email">
            <Input value={s.email} onChange={(e) => upd("email", e.target.value)}
              className="h-10" data-testid="s-email"/>
          </Field>
          <div className="md:col-span-2">
            <Field label="Address">
              <Textarea rows={2} value={s.address}
                onChange={(e) => upd("address", e.target.value)} data-testid="s-address"/>
            </Field>
          </div>
          <Field label="Logo">
            <ImageUpload testId="s-logo" value={s.logo} onChange={(v)=>upd("logo", v)} />
          </Field>
          <Field label="Signature">
            <ImageUpload testId="s-sig" value={s.signature}
              onChange={(v)=>upd("signature", v)} aspect="wide"/>
          </Field>
          <Field label="Stamp / Seal">
            <ImageUpload testId="s-stamp" value={s.stamp} onChange={(v)=>upd("stamp", v)} />
          </Field>
        </div>
      </div>

      {/* backup */}
      <div className="rounded-xl border border-slate-200 bg-white mt-6 p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">Backup & restore</h3>
            <p className="text-slate-500 text-sm mt-1 max-w-md">
              Export all students, courses, templates and settings as a single JSON file. Restore anytime.
            </p>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="application/json" className="hidden"
              onChange={(e) => e.target.files?.[0] && restore(e.target.files[0])} data-testid="restore-file"/>
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="restore-btn" className="h-10">
              <Upload size={16} className="mr-2"/> Restore
            </Button>
            <Button onClick={downloadBackup} data-testid="backup-btn" className="h-10 bg-blue-800 hover:bg-blue-900">
              <Download size={16} className="mr-2"/> Download backup
            </Button>
          </div>
        </div>
      </div>

      {/* password */}
      <div className="rounded-xl border border-slate-200 bg-white mt-6 p-6">
        <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
          <KeyRound size={18}/> Change password
        </h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          <Field label="Current password">
            <Input type="password" value={pw.current}
              onChange={(e) => setPw((x) => ({ ...x, current: e.target.value }))}
              className="h-10" data-testid="pw-current"/>
          </Field>
          <Field label="New password">
            <Input type="password" value={pw.next}
              onChange={(e) => setPw((x) => ({ ...x, next: e.target.value }))}
              className="h-10" data-testid="pw-new"/>
          </Field>
          <Field label="Confirm new password">
            <Input type="password" value={pw.confirm}
              onChange={(e) => setPw((x) => ({ ...x, confirm: e.target.value }))}
              className="h-10" data-testid="pw-confirm"/>
          </Field>
        </div>
        <div className="mt-4">
          <Button onClick={changePw} data-testid="pw-save" className="bg-slate-900 hover:bg-slate-800 h-10">
            Update password
          </Button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-slate-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
